import { reducer } from "ts-action";
import { on } from "ts-action-immer";

import * as AC from "../actionCreators";
import {
  ColorCategories,
  TableColumns,
  TableRow,
  TableRowAllowSettings,
} from "../../../requests/table/types";
import { AdminTableName, Page, TableIdentifier } from "../types";
import {
  NumberIndexedDictionary,
  StringIndexedDictionary,
} from "../../../globalTypes";
import {
  adjustAdminColumnInputTypes,
  adjustAdminColumnNames,
} from "../../../requests/administration/utils";

export type TableSTORE = {
  columns: TableColumns;
  rowsByHash: StringIndexedDictionary<TableRow>;
  totalRowCount: number;
  allowSettings: TableRowAllowSettings;
  colorCategories?: ColorCategories;
};

export type EditableTableSTORE = {
  columns: TableColumns;
  emptyEditRow: TableRow | null;
  rowsByHash: StringIndexedDictionary<TableRow>;
};

export type TableData = TableIdentifier & {
  // Ten null tam je proto, abych poznal initial fetch
  table: null | TableSTORE;
  editableTable: null | EditableTableSTORE; // editovatelná verze tabulky = s nenaformátovanými hodnotami
  pages: NumberIndexedDictionary<Page>;
  isFetchingEmptyEditRow: boolean;
  idsOfFetchingPages: number[];
  paramsHashForLastTableFetch: string | null;
  hashesOfFetchingEditRows: string[];
  hashesOfSubmittingEditRows: string[];
  rowsPerPage: number | null;
  currentPage: number;
};

export const makeTableInitialState = (
  identifier: TableIdentifier
): TableData => ({
  ...identifier,
  table: null,
  editableTable: null,
  pages: {},
  isFetchingEmptyEditRow: false,
  idsOfFetchingPages: [],
  paramsHashForLastTableFetch: null,
  hashesOfFetchingEditRows: [],
  hashesOfSubmittingEditRows: [],
  rowsPerPage: null,
  currentPage: 1,
});

// Tenhle initial state by se ve skutečnosti nikdy neměl použít - parent redeucer by měl
// vždy podstrčit default state s dopočítaným pagingem, pokud dict recordještě neexistuje
const dummyIdentifier: TableIdentifier = { kind: "template", idWithinKind: -1 };
const initialState: TableData = makeTableInitialState(dummyIdentifier);

const getInitialTable = (params: Partial<TableSTORE> = {}): TableSTORE => ({
  columns: {},
  rowsByHash: {},
  allowSettings: {} as TableRowAllowSettings,
  totalRowCount: 0,
  ...params,
});

const forTableId = reducer(
  initialState,
  on(AC.tableRequestPages, (state, { payload }) => {
    const { pages, fetchParamsHash, isOldDataObsolete, isPrefetch } = payload;

    if (isPrefetch) {
      state.idsOfFetchingPages = pages;
      return;
    }

    state.paramsHashForLastTableFetch = fetchParamsHash;

    if (isOldDataObsolete) {
      state.pages = {};
      state.idsOfFetchingPages = pages;
      return;
    }

    state.idsOfFetchingPages = [...state.idsOfFetchingPages, ...pages];
  }),

  on(AC.tableReceivePages, (state, { payload }) => {
    const {
      kind,
      idWithinKind,
      pagesByPageNumber,
      totalRowCount,
      columns,
      pages,
      allowSettings,
      colorCategories,
    } = payload;

    if (state.table === null) {
      state.table = getInitialTable({
        totalRowCount,
        columns,
        allowSettings,
        colorCategories,
      });
    }

    state.kind = kind;
    state.idWithinKind = idWithinKind;
    state.table.totalRowCount = totalRowCount;
    state.table.columns = columns;
    state.table.allowSettings = allowSettings;

    Object.keys(pagesByPageNumber)
      .map(Number)
      .forEach((pageNumber) => {
        const rowsOnCurrentPage = pagesByPageNumber[pageNumber];

        state.pages[pageNumber] = [];

        rowsOnCurrentPage.forEach((row, index) => {
          state.pages[pageNumber][index] = row.hash;
          if (state.table) {
            state.table.rowsByHash[row.hash] = row;
            state.table.colorCategories = colorCategories;
          }
        });
      });

    state.idsOfFetchingPages = state.idsOfFetchingPages.filter(
      (id) => !pages.includes(id)
    );
  }),

  on(AC.tableErrorWhenFetchingPages, (state, { payload }) => {
    const { pages } = payload;
    state.idsOfFetchingPages = state.idsOfFetchingPages.filter(
      (id) => !pages.includes(id)
    );
  }),

  on(AC.tableRequestFirstNRows, (state, action) => {
    const { fetchParamsHash } = action.payload;

    state.paramsHashForLastTableFetch = fetchParamsHash;
    state.pages = {}; // requestFirstN beru jako globální refetch pro mobil a vždy pro něj zahazuju stará data
  }),

  on(AC.tableReceiveFirstNRows, (state, { payload }) => {
    const { totalRowCount, columns, rows, allowSettings, colorCategories } =
      payload;
    const { rowsPerPage } = state;

    if (!rowsPerPage) return;
    if (state.table === null) {
      state.table = getInitialTable({
        totalRowCount,
        columns,
        allowSettings,
        colorCategories,
      });
    }

    state.table.totalRowCount = totalRowCount;
    state.table.columns = columns;
    state.table.allowSettings = allowSettings;
    state.table.colorCategories = colorCategories;
    state.pages = {};

    rows.forEach((row, idx) => {
      const pageNumber = Math.floor(idx / rowsPerPage) + 1;
      const positionOnPage = (idx % rowsPerPage) + 1;

      if (!state.pages[pageNumber]) state.pages[pageNumber] = [];
      state.pages[pageNumber][positionOnPage] = row.hash;
      if (state.table) {
        state.table.rowsByHash[row.hash] = row;
      }
    });
  }),

  on(AC.tableChangeCurrentPage, (state, { payload }) => {
    const { currentPage } = payload;
    state.currentPage = currentPage;
  }),

  on(AC.tableChangeRowsPerPage, (state, { payload }) => {
    const { rowsPerPage } = payload;
    state.rowsPerPage = rowsPerPage;
    state.currentPage = 1;
    state.pages = {};
    state.idsOfFetchingPages = [];
  }),

  on(AC.tableDropPaging, (state, { payload }) => {
    state.pages = {};
  }),

  on(AC.relocalizeAdminTableHeaders, (state) => {
    const adminTableName = state.idWithinKind as AdminTableName;

    const fullColumnsAdjust = (columns: TableColumns) =>
      adjustAdminColumnInputTypes(
        adjustAdminColumnNames(columns, adminTableName),
        adminTableName
      );

    if (state.table?.columns) {
      state.table.columns = fullColumnsAdjust(state.table.columns);
    }

    if (state.editableTable?.columns) {
      state.editableTable.columns = fullColumnsAdjust(
        state.editableTable.columns
      );
    }
  }),

  on(AC.tableRequestEditableRow, (state, { payload }) => {
    const { rowHash } = payload;
    state.hashesOfFetchingEditRows.push(rowHash);
  }),

  on(AC.tableReceiveEditableRow, (state, { payload }) => {
    const { rowHash, row, columns } = payload;

    if (!state.editableTable) {
      state.editableTable = {
        columns,
        emptyEditRow: null,
        rowsByHash: {},
      };
    }

    state.editableTable.columns = columns;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    state.editableTable.rowsByHash[rowHash] = row;
    state.hashesOfFetchingEditRows = state.hashesOfFetchingEditRows.filter(
      (hash) => hash !== rowHash
    );
  }),

  on(AC.tableErrorEditableRow, (state, { payload }) => {
    const { rowHash } = payload;
    state.hashesOfFetchingEditRows = state.hashesOfSubmittingEditRows.filter(
      (hash) => hash !== rowHash
    );
  }),

  on(AC.tableRequestEditableRowSubmit, (state, { payload }) => {
    const { rowHash } = payload;
    state.hashesOfSubmittingEditRows.push(rowHash);
  }),

  on(
    AC.tableReceiveEditableRowSubmit,
    AC.tableErrorEditableRowSubmit,
    (state, { payload }) => {
      const { rowHash } = payload;
      state.hashesOfSubmittingEditRows =
        state.hashesOfSubmittingEditRows.filter((hash) => hash !== rowHash);
    }
  ),

  on(AC.tableReceiveEmptyRow, (state, { payload }) => {
    const { row, columns } = payload;

    if (!state.editableTable) {
      state.editableTable = {
        columns,
        emptyEditRow: row,
        rowsByHash: {},
      };
    }

    state.editableTable.columns = columns;
    state.editableTable.emptyEditRow = row;
    state.isFetchingEmptyEditRow = true;
  }),

  on(AC.tableErrorEmptyRow, (state, { payload }) => {
    state.isFetchingEmptyEditRow = false;
  })
);

export default forTableId;
